import { useEffect, useRef, RefObject } from 'react';

interface UsePullToCloseOptions {
  onClose: () => void;
  threshold?: number;
  enabled?: boolean;
}

export function usePullToClose(
  scrollContainerRef: RefObject<HTMLElement>,
  { onClose, threshold = 60, enabled = true }: UsePullToCloseOptions
) {
  const startY = useRef(0);
  const startX = useRef(0);
  const hasTriggered = useRef(false);

  useEffect(() => {
    const el = scrollContainerRef.current;
    
    // Validación robusta: elemento debe existir y estar habilitado
    if (!enabled || !el) return;

    // 🎯 UX de producción: Reset instantáneo cuando se abre/reabre la sidebar
    // Similar a Facebook - siempre empieza desde arriba para contexto fresco
    const resetScroll = () => {
      try {
        // Limpiar transformaciones CSS que puedan estar aplicadas
        el.style.transform = 'translateY(0px)';
        el.style.transition = 'none';
        
        // Reset del scroll (scroll actual)
        el.scrollTo({ top: 0, behavior: 'auto' });
        el.scrollTop = 0;
      } catch (error) {
        // Fallback silencioso
        try {
          el.scrollTop = 0;
        } catch (e) {
          // Ignorar si falla
        }
      }
    };

    // Reset inmediato al montar/habilitar
    resetScroll();
    hasTriggered.current = false;

    // Validación del threshold: debe ser un número positivo válido
    // Threshold de 70px para detectar pulls horizontales genuinos
    const safeThreshold = typeof threshold === 'number' && threshold > 0 && isFinite(threshold) 
      ? threshold
      : 70;

    const handleTouchStart = (e: TouchEvent) => {
      // Protección: verificar que existan touches y sea un toque único
      if (!e.touches || e.touches.length !== 1) return;
      
      const touch = e.touches[0];
      if (!touch) return;

      startY.current = touch.clientY;
      startX.current = touch.clientX;
      hasTriggered.current = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      // Early returns para optimización y seguridad
      if (hasTriggered.current) return;
      if (!e.touches || e.touches.length !== 1) return;
      if (!el) return;

      const touch = e.touches[0];
      if (!touch) return;

      const currentY = touch.clientY;
      const currentX = touch.clientX;
      
      // Protección contra valores inválidos
      if (!isFinite(currentY) || !isFinite(currentX)) return;
      if (!isFinite(startY.current) || !isFinite(startX.current)) return;

      const diffY = currentY - startY.current;
      const diffX = currentX - startX.current;

      // 🎯 VALIDACIÓN ESTRICTA: 
      // 1. El scroll DEBE estar exactamente en 0 (no solo cerca de 0)
      // 2. El usuario DEBE estar haciendo un pull genuino (no scroll normal)
      const scrollTop = el.scrollTop;
      
      // Exigir que scrollTop esté exactamente en 0, NO en negativo ni positivo
      if (scrollTop !== 0) {
        // Si el usuario comenzó en 0 pero ahora no, resetear el punto de inicio
        // para que tenga que empezar el gesto de nuevo desde la nueva posición
        startY.current = currentY;
        startX.current = currentX;
        return;
      }

      // Solo si está EXACTAMENTE en top=0, evaluar el pull
      // const isPullDown = diffY > safeThreshold && diffY > Math.abs(diffX);
      const isPullRight = diffX > safeThreshold && diffX > Math.abs(diffY);

      if (isPullRight) {
        // Bloqueamos el scroll nativo inmediatamente
        if (e.cancelable) {
          try {
            e.preventDefault();
          } catch (error) {
            // Ignorar si preventDefault falla
          }
        }
        
        hasTriggered.current = true;
        
        // Ejecutar onClose de forma segura
        try {
          onClose();
        } catch (error) {
          console.error('[usePullToClose] Error al ejecutar onClose:', error);
        }
      }
    };

    // Añadir event listeners con manejo de errores
    try {
      el.addEventListener('touchstart', handleTouchStart, { passive: true });
      el.addEventListener('touchmove', handleTouchMove, { passive: false });
    } catch (error) {
      console.error('[usePullToClose] Error al añadir event listeners:', error);
      return;
    }

    // Cleanup robusto inspirado en Facebook
    return () => {
      try {
        el.removeEventListener('touchstart', handleTouchStart);
        el.removeEventListener('touchmove', handleTouchMove);
        
        // 🎯 Reset completo: scroll + CSS transforms
        // Necesario porque el sidebar puede haber sido transformado por el gesto
        el.style.transform = 'translateY(0px)';
        el.style.transition = 'none';
        el.scrollTop = 0;
        
        if (typeof el.scrollTo === 'function') {
          el.scrollTo({ top: 0, behavior: 'auto' });
        }
        
        // Pequeña demora para asegurar que el navegador procese los cambios
        // antes de que React desmonte el componente
        setTimeout(() => {
          try {
            el.style.transform = 'translateY(0px)';
            el.scrollTop = 0;
          } catch (error) {
            // Ignorar si el elemento ya fue removido del DOM
          }
        }, 0);
      } catch (error) {
        // Ignorar errores en cleanup
      }
    };
  }, [enabled, onClose, threshold, scrollContainerRef]);
}